import React from 'react'

import {
  AlignItems,
  Box,
  ButtonColorVariant,
  Color,
  Display,
  Heading3,
  Heading6,
  Icon,
  IconVariant,
  Modal,
  ModalBody,
  ModalClose,
  ModalFooter,
  ModalHeader,
  useModal,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import type { DeliveryDayOption } from '@library/api-customer-engagement-experience'

import { ModalConfirmButton } from './ConfirmButtons'
import type { Theme } from './theme'
import { useStagedSelection } from './useStagedSelection'
import type { CalendarDeliveryDay } from './useDeliveryDayCalendar'
import { useDeliveryDayCalendar } from './useDeliveryDayCalendar'

export const DELIVERY_DAY_MODAL_NAME = 'RESUBSCRIBE-DELIVERY-DAY-MODAL'

type DeliveryDayModalProps = {
  options: DeliveryDayOption[]
  selectedDaySlotLeadTimeId: string
  title: string
  deliveryDayRangeLabel?: string
  onSelect: (daySlotLeadTimeId: string) => void
}

const formatDayOfMonth = (date: string) => new Date(date).getUTCDate()

// Calendar table, cell and day button styling.
const CalendarTable = withCitrusTheme('table', () => ({
  width: '100%',
  borderCollapse: 'collapse',
  marginBottom: '1rem',
}))

const CalendarHeaderRow = withCitrusTheme('thead', (theme) => ({
  backgroundColor: theme.color(Color.Secondary_50),
  border: `1px solid ${theme.color(Color.ColdGrey_100)}`,
}))

const CalendarHeaderCell = withCitrusTheme('th', () => ({
  textAlign: 'center',
  width: `${100 / 7}%`,
  height: '1.5rem',
}))

const CalendarCell = withCitrusTheme('td', (theme) => ({
  border: `1px solid ${theme.color(Color.ColdGrey_100)}`,
  padding: 0,
}))

const getCalendarDayButtonStyle = (theme: Theme, selected: boolean) => ({
  position: 'relative' as const,
  display: 'block',
  width: '100%',
  aspectRatio: '1 / 1',
  background: `${theme.color(selected ? Color.Secondary_400 : Color.White)}`,
  color: selected ? `${theme.color(Color.White)}` : undefined,
  border: 'none',
  cursor: 'pointer',
})

const PlainDayButton = withCitrusTheme('button', (theme) => getCalendarDayButtonStyle(theme, false))

const SelectedDayButton = withCitrusTheme('button', (theme) =>
  getCalendarDayButtonStyle(theme, true),
)

const CalendarDayButton = ({
  selected,
  ...props
}: { selected: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) => {
  const DayButton = selected ? SelectedDayButton : PlainDayButton

  return <DayButton {...props} />
}

export const DeliveryDayModal = ({
  options,
  selectedDaySlotLeadTimeId,
  title,
  deliveryDayRangeLabel,
  onSelect,
}: DeliveryDayModalProps) => {
  const { closeCurrentModal } = useModal()
  const { getCalendarWeekDays, getCalendarDays } = useDeliveryDayCalendar(options)
  const [stagedDaySlotLeadTimeId, setStagedDaySlotLeadTimeId] = useStagedSelection(
    DELIVERY_DAY_MODAL_NAME,
    selectedDaySlotLeadTimeId,
  )
  const stagedDay = options.find((option) => option.daySlotLeadTimeId === stagedDaySlotLeadTimeId)

  const handleDayClick = (day: CalendarDeliveryDay) => () => {
    if (day.disabled) {
      return
    }

    setStagedDaySlotLeadTimeId(day.daySlotLeadTimeId)
  }

  const canSave = stagedDaySlotLeadTimeId !== selectedDaySlotLeadTimeId

  const handleSaveClick = () => {
    onSelect(stagedDaySlotLeadTimeId)
    closeCurrentModal()
  }

  return (
    <Modal name={DELIVERY_DAY_MODAL_NAME} onRequestClose={closeCurrentModal}>
      <ModalHeader>
        <Heading3>{title}</Heading3>
        <ModalClose onClose={closeCurrentModal} />
      </ModalHeader>
      <ModalBody>
        {deliveryDayRangeLabel && (
          <Box paddingBottom={3} display={Display.Flex} alignItems={AlignItems.Center} gap={2}>
            <Icon name="calendar" size={4} variant={IconVariant.Default} />
            <Heading6>{deliveryDayRangeLabel}</Heading6>
          </Box>
        )}
        <CalendarTable>
          <CalendarHeaderRow>
            <tr>
              {getCalendarWeekDays().map((weekDay, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <CalendarHeaderCell key={`${weekDay}${index}`}>{weekDay}</CalendarHeaderCell>
              ))}
            </tr>
          </CalendarHeaderRow>
          <tbody>
            {getCalendarDays().map((week, weekIndex) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={`week-${weekIndex}`}>
                {week.map((day, index) =>
                  !day ? (
                    // eslint-disable-next-line react/no-array-index-key
                    <CalendarCell key={`empty-day-${index}`} />
                  ) : (
                    <CalendarCell key={day.daySlotLeadTimeId || day.date}>
                      <CalendarDayButton
                        type="button"
                        selected={stagedDaySlotLeadTimeId === day.daySlotLeadTimeId}
                        disabled={day.disabled}
                        onClick={handleDayClick(day)}
                      >
                        {formatDayOfMonth(day.date)}
                      </CalendarDayButton>
                    </CalendarCell>
                  ),
                )}
              </tr>
            ))}
          </tbody>
        </CalendarTable>
      </ModalBody>
      <ModalFooter>
        <ModalConfirmButton
          colorVariant={ButtonColorVariant.Primary}
          width="100%"
          disabled={!canSave}
          onClick={handleSaveClick}
        >
          {stagedDay?.selectLabel}
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  )
}
